import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

const SEO = ({ 
  title = "MassHappy · Terapeutas, ritual diario y retiros",
  description = "Terapeutas verificados cerca de ti, un ritual diario en la app y retiros en Mallorca. Bienestar real, sin promesas milagrosas.",
  keywords = "terapias salud, bienestar, masajes, fisioterapia, yoga, pilates, breathwork, meditación, reiki, tai chi, terapeutas certificados, España",
  image = "https://www.masshappy.com/__l5e/assets-v1/fd6ddb62-e78b-49e7-ad2b-d299ceb395d5/masshappy-og-image.png",
  url = "https://masshappy.com",
  type = "website",
  noindex = false,
  jsonLd
}: SEOProps) => {
  const fullTitle = title.toLowerCase().includes("masshappy") ? title : `${title} | MassHappy`;
  // Las redes sociales necesitan URL absoluta en og:image
  const absoluteImage = image.startsWith("http") ? image : `https://masshappy.com${image.startsWith("/") ? "" : "/"}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="masshappy" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      {!noindex && <link rel="canonical" href={url} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:site_name" content="masshappy" />
      <meta property="og:locale" content="es_ES" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={absoluteImage} />

      {/* Additional SEO */}
      <meta name="theme-color" content="#E53E3E" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="masshappy" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "MassHappy",
          "url": url,
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.masshappy.com/icon-512.png",
            "width": "512",
            "height": "512"
          },
          "description": description,
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "ES"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "info@masshappy.com"
          },
          "sameAs": [
            "https://www.facebook.com/masshappy",
            "https://www.twitter.com/masshappy",
            "https://www.instagram.com/masshappy"
          ]
        })}
      </script>

      {jsonLd &&
        (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((entry, i) => (
          <script key={i} type="application/ld+json">
            {JSON.stringify(entry)}
          </script>
        ))}
    </Helmet>
  );
};

export default SEO;